import { useState } from 'react';
import { uploadVideo, UploadStatus } from '../../../libs/uploaders/uploadVideo';
import { DetailedError } from 'tus-js-client';
import { toastError, toastSuccess, toastInfo } from '../../../libs/toasts/toasts';
import { toastsT } from '../../../i18n/toasts.i18n';
import { MAX_VIDEO_TITLE_LENGTH } from '../../../constants/MAX_VIDEO_TITLE_LENGTH';
import { getDefaultProfile } from '../../../libs/utils/getProfileOptions';
import { vzhikLogger } from '../../../libs/vzhik-logger/VzhikLogger';
import { ProfilesRecord } from '../../Page/types/ProfilesRecord';

export type UploadState = {
    file: File | null;
    videoTitle: string;
    profile: string;
    enableSummarization: boolean;
    isUploading: boolean;
    progress: number;
    currentUploadPromise: Promise<{
        abort: () => Promise<void>;
        start: () => Promise<void>;
    } | null>;
};

const makeInitialState = (profiles: ProfilesRecord): UploadState => ({
    file: null,
    videoTitle: '',
    profile: getDefaultProfile(profiles),
    enableSummarization: false,
    isUploading: false,
    progress: 0,
    currentUploadPromise: Promise.resolve(null),
});

type UseMobileUploadParams = {
    profiles: ProfilesRecord;
    onUpload: () => void;
    onUploadStart?: (uploadUrl: string) => void;
};

export function useMobileUpload({ profiles, onUpload, onUploadStart }: UseMobileUploadParams) {
    const [uploadState, setUploadState] = useState<UploadState>(() => makeInitialState(profiles));
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = true;
    };

    const handleFileSelected = (files: File[]) => {
        if (files.length === 0) {
            return;
        }
        const file = files[0];
        setUploadState((prev) => ({
            ...prev,
            file,
            videoTitle: file.name,
            profile: prev.profile || getDefaultProfile(profiles),
        }));
    };

    const handleStatusChange = (props: {
        status?: UploadStatus;
        progress?: number;
        error?: Error | DetailedError;
    }) => {
        switch (props.status) {
            case 'resume':
                toastInfo.open(toastsT('resumeUpload'));
                break;
            case 'error':
                setUploadState((prev) => ({ ...prev, isUploading: false }));
                toastError.open(toastsT('uploadFailed'));
                vzhikLogger.logError({
                    message: 'Не удалось загрузить видео (мобайл)',
                    type: 'server',
                    additional: { error: props.error },
                });
                window.removeEventListener('beforeunload', handleBeforeUnload);
                break;
            case 'success':
                setUploadState(makeInitialState(profiles));
                toastSuccess.open(toastsT('videoUploaded'));
                setIsModalOpen(false);
                onUpload();
                window.removeEventListener('beforeunload', handleBeforeUnload);
                break;
            case 'progress':
                setUploadState((prev) => ({ ...prev, progress: props.progress ?? 0 }));
                break;
            default:
                break;
        }
    };

    const handleSubmit = async () => {
        if (!uploadState.file || !uploadState.profile) {
            return;
        }

        setUploadState((prev) => ({ ...prev, isUploading: true }));
        window.addEventListener('beforeunload', handleBeforeUnload);

        const currentUploadPromise = uploadVideo({
            file: uploadState.file,
            videoTitle:
                uploadState.videoTitle ||
                uploadState.file.name.slice(0, MAX_VIDEO_TITLE_LENGTH - 1),
            profile: uploadState.profile,
            enableSummarization: uploadState.enableSummarization,
            onUploadStateChange: handleStatusChange,
            onUploadStart,
        });

        setUploadState((prev) => ({ ...prev, currentUploadPromise }));

        const uploadPromise = await currentUploadPromise;
        if (uploadPromise) {
            await uploadPromise.start();
        }
    };

    const handleCancel = () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        uploadState.currentUploadPromise.then((u) => u?.abort());
        setUploadState(makeInitialState(profiles));
        setIsModalOpen(false);
    };

    const openModal = () => {
        setUploadState((prev) => ({
            ...prev,
            profile: prev.profile || getDefaultProfile(profiles),
        }));
        setIsModalOpen(true);
    };

    const updateField = <K extends keyof UploadState>(key: K, value: UploadState[K]) => {
        setUploadState((prev) => ({ ...prev, [key]: value }));
    };

    return {
        uploadState,
        isModalOpen,
        openModal,
        handleFileSelected,
        handleSubmit,
        handleCancel,
        updateField,
    };
}
